import { useCallback, useEffect, useState } from 'react';
import UrlForm from './components/UrlForm.jsx';
import AnalysisResult from './components/AnalysisResult.jsx';
import NodePanel from './components/NodePanel.jsx';
import { api } from './lib/api.js';

export default function App() {
  const [countries, setCountries] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [health, setHealth] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [checking, setChecking] = useState(false);
  const [probing, setProbing] = useState(false);

  const loadPool = useCallback(async () => {
    try {
      const [nodesPayload, countriesPayload, healthPayload] = await Promise.all([
        api.listNodes(),
        api.listCountries(),
        api.health(),
      ]);
      setNodes(nodesPayload.nodes);
      setCountries(countriesPayload.countries);
      setHealth(healthPayload);
    } catch (loadError) {
      setError(`Could not reach the gateway API: ${loadError.message}`);
    }
  }, []);

  useEffect(() => {
    void loadPool();
    // Health sweeps run server-side every minute; mirror that in the UI.
    const timer = setInterval(() => void loadPool(), 60_000);
    return () => clearInterval(timer);
  }, [loadPool]);

  const handleCheck = async (url, country) => {
    setChecking(true);
    setError(null);
    setResult(null);
    try {
      setResult(await api.checkUrl(url, country));
    } catch (checkError) {
      setError(checkError.message);
    } finally {
      setChecking(false);
    }
  };

  const handleProbe = async () => {
    setProbing(true);
    setError(null);
    try {
      const payload = await api.checkAllNodes();
      setNodes(payload.nodes);
      const countriesPayload = await api.listCountries();
      setCountries(countriesPayload.countries);
    } catch (probeError) {
      setError(probeError.message);
    } finally {
      setProbing(false);
    }
  };

  return (
    <div className="app">
      <header className="header">
        <h1 className="header__title">
          <span className="header__badge">🌍</span>
          Geo-Access Gateway
        </h1>
        <p className="header__subtitle">
          Paste a link that is blocked in your country. The gateway checks whether it is really
          geo-restricted, then re-fetches it — and every asset and link inside it — through one of
          your own servers abroad.
        </p>
        {health && (
          <span className="header__status">
            <span className="dot dot--ok" />
            API online · {health.nodes} node{health.nodes === 1 ? '' : 's'} ·{' '}
            {health.countries} countr{health.countries === 1 ? 'y' : 'ies'}
          </span>
        )}
      </header>

      <section className="card">
        <h2 className="card__title">Check a link</h2>
        <p className="card__hint">
          The gateway fetches the URL twice — once directly, once through the country you pick — and
          compares the two responses.
        </p>
        <UrlForm countries={countries} onCheck={handleCheck} busy={checking} />
      </section>

      {error && <div className="alert">{error}</div>}

      {result && <AnalysisResult result={result} />}

      <NodePanel nodes={nodes} onRefresh={handleProbe} busy={probing} />

      <footer className="footer">
        Geo-Access Gateway — graduation project. Use it only with content you are entitled to access.
      </footer>
    </div>
  );
}
